import { Stack, Table, TableBody, TableRow, Typography } from '@mui/material';
import { maxBy, sortBy } from 'lodash';
import { ReactNode, useMemo } from 'react';

import {
    SoundPressureBandStatsSample,
    SoundPressureQuietSample,
    SoundPressureReading,
    SoundPressureSample,
    SoundPressureSampleStatsDatum,
} from '@/api/historicalData';
import { hydrophoneIcon } from '@/assets';
import { Datum } from '@/components/data/Datum';
import { IconData, LabeledData } from '@/components/data/IconData';
import { DataDetailTimestampRange } from '@/components/spotter/historicalDataTab/datumDetail/DataDetailSectionTimestamp';
import { MissingDataInfo } from '@/components/spotter/historicalDataTab/datumDetail/MissingDataInfo';
import { SelectableTimestampBox } from '@/components/spotter/historicalDataTab/datumDetail/SelectableTimestampBox';
import { useDateRangeContext } from '@/context/DateRangeContext';
import { boldWeight } from '@/theme';
import { isFiniteNumber } from '@/utils';

export const SpectrogramDatumDetail = ({
    data,
    ...detailProps
}: {
    data: SoundPressureSample | null;
    dataset: SoundPressureSample[];
    statKey: keyof SoundPressureSampleStatsDatum;
    statLabel: string;
}) => {
    if (!data) return false;

    switch (data?.dataQuality) {
        case 'absent':
            return <MissingDataSampleDetail data={data} />;
        case 'tooQuiet':
            return <QuietSampleDetail data={data} />;
        case 'present':
            return <BandStatsSampleDetail data={data} {...detailProps} />;
    }
};

const MissingDataSampleDetail = (_props: { data: SoundPressureSample }) => {
    return (
        <MissingDataInfo>
            Data for this time range is missing. This can be caused by issues with power, telemetry, transmission, or
            other factors.
        </MissingDataInfo>
    );
};

const QuietSampleDetail = ({ data }: { data: SoundPressureQuietSample }) => {
    return (
        <Stack>
            <BroadbandDataItem sample={data} />
            <MissingDataInfo>
                No per-band sound pressures were reported with this sample because the broadband SPL did not reach the
                reporting threshold.
            </MissingDataInfo>
        </Stack>
    );
};

const BandStatsSampleDetail = ({
    data,
    statKey,
    statLabel,
}: {
    data: SoundPressureBandStatsSample;
    statKey: keyof SoundPressureSampleStatsDatum;
    statLabel: string;
}) => {
    const sorted = useMemo(() => sortBy(data.bandStats, 'frequency'), [data]);
    const maxSplFloor = useMemo(
        () => (maxBy(data.bandStats, statKey)?.[statKey] ?? Number.POSITIVE_INFINITY) - 0.1,
        [data, statKey]
    );

    return (
        <Stack>
            <BroadbandDataItem sample={data} />
            {!!sorted?.length && (
                <LabeledData label={`${statLabel} SPL per band (re 1 µPa²)`}>
                    <Table sx={{ width: 'initial' }}>
                        <TableBody>
                            {sorted.map((datum) => {
                                const isMaxSpl = (datum[statKey] ?? 0) >= maxSplFloor;
                                const fontWeight = isMaxSpl ? boldWeight : undefined;
                                return (
                                    <TableRow key={datum.frequency}>
                                        <Typography
                                            component="td"
                                            variant="minorLabel"
                                            sx={{
                                                textAlign: 'right',
                                                py: 0,
                                                pr: 0.5,
                                                fontWeight,
                                            }}
                                        >
                                            {datum.frequency} Hz
                                        </Typography>
                                        <Typography
                                            component="td"
                                            sx={{
                                                textAlign: 'right',
                                                p: 0,
                                                border: 'none',
                                                fontWeight,
                                            }}
                                        >
                                            <Datum
                                                value={datum[statKey]}
                                                type="soundPressureLevel"
                                                sourceUnit="dB"
                                                padTrailingZeros
                                                sx={{ fontWeight }}
                                            />
                                        </Typography>
                                        {isMaxSpl && (
                                            <Typography component="td" variant="minorLabel" sx={{ pl: 0.5 }}>
                                                (peak)
                                            </Typography>
                                        )}
                                    </TableRow>
                                );
                            })}
                        </TableBody>
                    </Table>
                </LabeledData>
            )}
        </Stack>
    );
};

const BroadbandDataItem = ({
    sample,
}: {
    sample: SoundPressureQuietSample | SoundPressureBandStatsSample | SoundPressureReading;
}) => (
    <IconData src={hydrophoneIcon} label="Broadband SPL (re 1 µPa²)" dataSx={{ ml: '36px' }}>
        <Typography component="span">
            <Datum value={sample.broadbandSpl} type="soundPressureLevel" sourceUnit="dB" />
        </Typography>
    </IconData>
);

export const SpectrogramDataSummary = ({
    dataset,
    statKey,
    statLabel,
}: {
    dataset: SoundPressureSample[];
    statKey: keyof SoundPressureSampleStatsDatum;
    statLabel: string;
}) => {
    const { dateRange } = useDateRangeContext();
    const peakSamples = useMemo(() => {
        /*
         * A running peak, so the pass holds one entry per frequency. A frequency whose every sample is missing
         * this stat gets no entry at all, which is what keeps `min` and `max` out of the table for a dataset
         * that reports no whiskers.
         */
        const peakByFrequency = new Map<number, { timestamp: string; band: SoundPressureSampleStatsDatum }>();

        for (const sample of dataset) {
            if (sample.dataQuality !== 'present') {
                continue;
            }
            for (const band of sample.bandStats) {
                const value = band[statKey];
                // the band held for a frequency is the one whose stat is the peak, so it carries the comparand
                if (
                    isFiniteNumber(value) &&
                    value > (peakByFrequency.get(band.frequency)?.band[statKey] ?? -Infinity)
                ) {
                    peakByFrequency.set(band.frequency, { timestamp: sample.timestamp, band });
                }
            }
        }

        return sortBy([...peakByFrequency.values()], (sample) => sample.band.frequency);
    }, [dataset, statKey]);
    const maxSplFloor = useMemo(
        () => (maxBy(peakSamples, (s) => s.band[statKey])?.band?.[statKey] ?? Number.POSITIVE_INFINITY) - 0.1,
        [peakSamples, statKey]
    );

    if (!peakSamples.length) {
        return false;
    }

    return (
        <>
            {dateRange && <DataDetailTimestampRange dateRange={dateRange} />}
            <Stack sx={{ m: 1 }}>
                <LabeledData label={`Peak ${statLabel.toLowerCase()} SPL per band`}>
                    <Table sx={{ width: 'initial' }}>
                        <TableBody>
                            {peakSamples.map(({ timestamp, band }) => {
                                const isMaxSpl = (band[statKey] ?? 0) >= maxSplFloor;
                                const fontWeight = isMaxSpl ? boldWeight : undefined;
                                return (
                                    <SelectableTimestampBox
                                        key={band.frequency}
                                        component={TableRow}
                                        timestamp={timestamp}
                                    >
                                        <Typography
                                            component="td"
                                            variant="minorLabel"
                                            sx={{
                                                textAlign: 'right',
                                                py: 0,
                                                pr: 0.5,
                                                fontWeight,
                                            }}
                                        >
                                            {band.frequency} Hz
                                        </Typography>
                                        <Typography
                                            component="td"
                                            sx={{
                                                textAlign: 'right',
                                                p: 0,
                                                border: 'none',
                                                fontWeight,
                                            }}
                                        >
                                            <Datum
                                                value={band[statKey]}
                                                type="soundPressureLevel"
                                                sourceUnit="dB"
                                                padTrailingZeros
                                                sx={{ fontWeight }}
                                            />
                                        </Typography>
                                        {isMaxSpl && (
                                            <Typography component="td" variant="minorLabel" sx={{ pl: 0.5 }}>
                                                (peak)
                                            </Typography>
                                        )}
                                    </SelectableTimestampBox>
                                );
                            })}
                        </TableBody>
                    </Table>
                </LabeledData>
            </Stack>
        </>
    );
};

export const SpectrogramBandReadingsDetail = ({ data }: { data: SoundPressureReading }) => {
    const sorted = useMemo(() => sortBy(data.bands, 'frequency'), [data]);
    const maxSplFloor = useMemo(() => (maxBy(data.bands, 'spl')?.spl ?? Number.POSITIVE_INFINITY) - 0.1, [data]);

    if (!data.bands.length && !isFiniteNumber(data.broadbandSpl)) {
        return <MissingDataInfo>No data for this reading time</MissingDataInfo>;
    }

    return (
        <Stack>
            <BroadbandDataItem sample={data} />
            {!!sorted?.length && (
                <LabeledData label={`SPL per band (re 1 µPa²)`}>
                    <Table sx={{ width: 'initial' }}>
                        <TableBody>
                            {sorted.map((datum) => {
                                const isMaxSpl = (datum.spl ?? 0) >= maxSplFloor;
                                const fontWeight = isMaxSpl ? boldWeight : undefined;
                                return (
                                    <TableRow key={datum.frequency}>
                                        <Typography
                                            component="td"
                                            variant="minorLabel"
                                            sx={{
                                                textAlign: 'right',
                                                py: 0,
                                                pr: 0.5,
                                                fontWeight,
                                            }}
                                        >
                                            {datum.frequency} Hz
                                        </Typography>
                                        <Typography
                                            component="td"
                                            sx={{
                                                textAlign: 'right',
                                                p: 0,
                                                border: 'none',
                                                fontWeight,
                                            }}
                                        >
                                            <Datum
                                                value={datum.spl}
                                                type="soundPressureLevel"
                                                sourceUnit="dB"
                                                padTrailingZeros
                                                sx={{ fontWeight }}
                                            />
                                        </Typography>
                                        {isMaxSpl && (
                                            <Typography component="td" variant="minorLabel" sx={{ pl: 0.5 }}>
                                                (peak)
                                            </Typography>
                                        )}
                                    </TableRow>
                                );
                            })}
                        </TableBody>
                    </Table>
                </LabeledData>
            )}
        </Stack>
    );
};

export const SpectrogramReadingsSummary = ({
    dataset,
    actions,
}: {
    dataset: SoundPressureReading[];
    actions?: ReactNode;
}): JSX.Element | false => {
    const dateRange = !dataset?.length
        ? null
        : { startDate: new Date(dataset[0].timestamp), endDate: new Date(dataset[dataset.length - 1].timestamp) };
    const peakReadings = useMemo(() => {
        // the same running peak as the sample summary above, over one reading per second rather than per sample
        const peakByFrequency = new Map<number, { timestamp: string; band: SoundPressureReading['bands'][number] }>();

        for (const { timestamp, bands } of dataset) {
            for (const band of bands ?? []) {
                if (
                    isFiniteNumber(band.spl) &&
                    band.spl > (peakByFrequency.get(band.frequency)?.band.spl ?? -Infinity)
                ) {
                    peakByFrequency.set(band.frequency, { timestamp, band });
                }
            }
        }

        return sortBy([...peakByFrequency.values()], (reading) => reading.band.frequency);
    }, [dataset]);
    const maxSplFloor = useMemo(
        () => (maxBy(peakReadings, ({ band }) => band.spl)?.band?.spl ?? Number.POSITIVE_INFINITY) - 0.1,
        [peakReadings]
    );

    if (!peakReadings.length || !dateRange) {
        return false;
    }

    return (
        <>
            <DataDetailTimestampRange dateRange={dateRange} unit="datetime" processingSource="hdr" />
            {actions}
            <Stack sx={{ m: 1 }}>
                <LabeledData label={`Peak SPL per band`}>
                    <Table sx={{ width: 'initial' }}>
                        <TableBody>
                            {peakReadings.map(({ timestamp, band }) => {
                                const isMaxSpl = (band.spl ?? 0) >= maxSplFloor;
                                const fontWeight = isMaxSpl ? boldWeight : undefined;
                                return (
                                    <SelectableTimestampBox
                                        key={band.frequency}
                                        component={TableRow}
                                        subsample
                                        includeSeconds
                                        timestamp={timestamp}
                                    >
                                        <Typography
                                            component="td"
                                            variant="minorLabel"
                                            sx={{
                                                textAlign: 'right',
                                                py: 0,
                                                pr: 0.5,
                                                fontWeight,
                                            }}
                                        >
                                            {band.frequency} Hz
                                        </Typography>
                                        <Typography
                                            component="td"
                                            sx={{
                                                textAlign: 'right',
                                                p: 0,
                                                border: 'none',
                                                fontWeight,
                                            }}
                                        >
                                            <Datum
                                                value={band.spl}
                                                type="soundPressureLevel"
                                                sourceUnit="dB"
                                                padTrailingZeros
                                                sx={{ fontWeight }}
                                            />
                                        </Typography>
                                        {isMaxSpl && (
                                            <Typography component="td" variant="minorLabel" sx={{ pl: 0.5 }}>
                                                (peak)
                                            </Typography>
                                        )}
                                    </SelectableTimestampBox>
                                );
                            })}
                        </TableBody>
                    </Table>
                </LabeledData>
            </Stack>
        </>
    );
};
